/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useRef } from "react";
import {
  BookOpen, Compass, Search, Bookmark, Heart, Mail,
  Sparkles, Feather, ArrowRight, ArrowLeft, Sun, Moon, Tag as TagIcon, PenLine, LogOut, Check,
  ScrollText, Flower2, LibraryBig, Info, Menu, X
} from "lucide-react";
import { GoogleLogo } from "@phosphor-icons/react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { toast, Toaster } from "sonner";
import confetti from "canvas-confetti";
import "./App.css";
import AuthorDesk from "./AuthorDesk";
import ShareBar from "./components/ShareBar";
import { renderEssayHtml } from "./richHtml";
import AuthCallback from "./components/AuthCallback";
import AudioPlayer from "./components/AudioPlayer";
import ReaderProfile from "./components/ReaderProfile";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GUEST_KEY = "mm-guest";
const mediaSrc = (url) => (url && url.startsWith("http") ? url : `${API_URL}${url}`);

const AUTHOR = { name: "Shailla Khan", role: "Writer, Researcher & Contemplative", initials: "SK" };

// Fallback copy, the backend serves the same defaults until the author edits them in the desk
const DEFAULT_SITE_TEXTS = {
  hero: {
    kicker: "Contemplative Publication & Sanctuary",
    line1: "Where the human mind",
    highlight: "divine balance",
    intro: "A personal publication by Shailla Khan exploring the intersections of Qur'anic reflection, modern neuroscience, depth psychology, and the quiet art of conscious character.",
  },
  manifesto: {
    subtitle: "Why Mind & Mizan exists in an age of algorithmic noise and digital fragmentation.",
    banner_quote: "A space where the Qur'an is explored alongside the human mind, without forcing either side to say more than it actually does.",
    banner_subtext: "Quiet, reflective, intellectually curious, spiritually grounded, an invitation to think, not a lecture on what to think.",
    chapters: [
      { title: "Why I started", text: "I kept noticing that people separate spirituality from psychology, as if they belong to different worlds. Yet the questions that keep us up at night, anxiety, grief, envy, self-worth, live in both. I started Mind & Mizan because I wanted one quiet place where the Qur'an could be explored alongside the human mind: thoughtfully, honestly, and without forcing either side to say more than it actually does." },
      { title: "The question I keep returning to", text: "What does the Qur'an teach us about being human? Not only what it asks us to do, but why these teachings matter, psychologically, emotionally. Mizan (balance), sakinah, sabr, forgiveness, purpose, human nature: I return to these because they describe the inner life with an accuracy I keep finding confirmed, in reflection, and in honest research." },
      { title: "On honesty with sources", text: "I try to keep clear distinctions: what is Qur'an, what is Prophetic hadith and Sunnah, what is Hadith Qudsi, and what is the insight of the Companions or later scholars. A verse should never be stretched merely to fit a modern theory, and where I draw on neuroscience or psychology, the research deserves to be represented honestly. Science is not here to \"prove\" religion, and religion does not need to fear sincere questions." },
      { title: "How I write, and what I hope for you", text: "I write in simple, clear English, because depth should not require difficulty. Most essays begin with a question or an observation from ordinary life, then move slowly, through Qur'anic reflection, scholarship, and research, toward practical meaning. My hope is modest: that you pause. That you recognise something of yourself here, see a familiar struggle through a different lens, and leave with one idea you can carry into your life. This is an invitation to think, not a lecture on what to think." },
    ],
    closing_quote: "Verily, in the remembrance of God do hearts find rest.",
    humility_title: "A Note of Humility",
    humility: "Mind & Mizan is a learning space, not a place of religious authority. We are human, and our understanding is limited. Errors, omissions, or misunderstandings are possible.\n\nAny error in our writing, translation, interpretation, or explanation is our own and should never be attributed to the Quran or to Allah's revelation.\n\nWe make every effort to verify Quranic references, translations, tafsir, hadith, asbab al-nuzul, and scholarly sources. Where scholars differ or a report is uncertain, we aim to make that clear.\n\nOur reflections are offered for learning and contemplation. They are not revelation, fatwas, or definitive religious rulings, and they do not replace qualified scholarship.\n\n\"And above every possessor of knowledge is One more knowing.\"\nQuran 12:76\n\nAll knowledge ultimately belongs to Allah.\n\nWe are all learning. We read, reflect, question, correct ourselves, and continue to learn.\n\nMind & Mizan is a learning journey for all of us.",
  },
  women: {
    intro: "A space to explore the inner and outer lives of women through faith, psychology, relationships, work, identity, and everyday life.",
  },
  about: {
    publication: "Mind & Mizan is a personal publication by Shailla Khan, exploring the intersections of Qur'anic reflection, modern neuroscience, depth psychology, and the quiet art of conscious character.\n\nEssays gather into named series, each one a slow walk through a single theme. The Mizan Dispatch carries one essay to subscribers each Friday.",
    author: "Shailla Khan is a writer and researcher exploring the human mind through faith and honest scholarship.\n\nA fuller introduction is being written.",
  },
};

const WOMEN_TOPICS = ["Mind & Emotions", "Faith & Inner Life", "Relationships", "Work & Identity", "Body & Life", "Motherhood", "Reflections"];

const DEFAULT_META = {
  title: "Mind & Mizan, Where the human mind meets divine balance",
  description: "Essays on the human mind, Qur'anic reflection, psychology, neuroscience, and the quiet art of character, by Shailla Khan.",
};

function setMetaTag(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function updatePageMeta(article) {
  if (article) {
    const url = `${window.location.origin}${window.location.pathname}?essay=${article.id}`;
    document.title = `${article.title}, Mind & Mizan`;
    setMetaTag("property", "og:title", `${article.title}, Mind & Mizan`);
    setMetaTag("property", "og:description", article.excerpt || DEFAULT_META.description);
    setMetaTag("property", "og:url", url);
    setMetaTag("name", "twitter:title", `${article.title}, Mind & Mizan`);
    setMetaTag("name", "twitter:description", article.excerpt || DEFAULT_META.description);
  } else {
    document.title = DEFAULT_META.title;
    setMetaTag("property", "og:title", DEFAULT_META.title);
    setMetaTag("property", "og:description", DEFAULT_META.description);
    setMetaTag("property", "og:url", window.location.origin + window.location.pathname);
  }
}

const EASE = [0.22, 1, 0.36, 1];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

function MaskedLine({ children, delay = 0, as: Tag = "span" }) {
  return (
    <span className="mask-line">
      <motion.span
        as={Tag}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const MARQUEE_WORDS = ["The Mind", "Qur'anic Reflection", "Psychology", "Neuroscience", "Character", "Life", "Tafakkur", "Mizan", "Silence", "Sabr", "Attention", "Presence"];

export default function App() {
  // Emergent Google OAuth returns here with #session_id=..., exchange it before anything renders.
  if (window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <MainApp />;
}

function MainApp() {
  const [activeTab, setActiveTab] = useState("home"); // home, articles, article-detail, philosophy, bookmarks, author
  const [selectedTopic, setSelectedTopic] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [isSubmittingNews, setIsSubmittingNews] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [likedIds, setLikedIds] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const [reader, setReader] = useState(null);
  const [fontSize, setFontSize] = useState("normal"); // normal, large, xlarge
  const [darkMode, setDarkMode] = useState(false);
  const lenisRef = useRef(null);

  // Smooth momentum scrolling
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  // Parallax for hero portrait
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 700], [0, 90]);
  const heroTextY = useTransform(scrollY, [0, 700], [0, -50]);

  // Fetch articles
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchArticles();
  }, [selectedTopic, searchQuery, selectedTag]);

  // Reader session restore (guests fall back to localStorage state)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const guest = JSON.parse(localStorage.getItem(GUEST_KEY) || "{}");
    setBookmarkedIds(guest.bookmarks || []);
    setLikedIds(guest.likes || []);
    fetch(`${API_URL}/api/auth/reader/me`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((user) => {
        setReader(user);
        setBookmarkedIds(user.bookmarks || []);
        setLikedIds(user.likes || []);
        setReadIds(user.history || []);
      })
      .catch(() => setReader(null));
  }, []);

  const saveGuestState = (bookmarks, likes) => {
    if (!reader) localStorage.setItem(GUEST_KEY, JSON.stringify({ bookmarks, likes }));
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startReaderSignIn = () => {
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const readerSignOut = async () => {
    await fetch(`${API_URL}/api/auth/reader/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setReader(null);
    setBookmarkedIds([]); setLikedIds([]); setReadIds([]);
    setMyFollows([]);
    localStorage.removeItem(GUEST_KEY);
    toast.info("Signed out. Your sanctuary waits for your return.");
  };

  // Reader follows (author + publication), quiet and publication-focused
  const [myFollows, setMyFollows] = useState([]);
  const [followCounts, setFollowCounts] = useState({ author: 0, publication: 0 });

  const fetchFollowCounts = () => {
    fetch(`${API_URL}/api/follows/counts`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setFollowCounts).catch(() => {});
  };

  useEffect(() => { fetchFollowCounts(); }, []);

  useEffect(() => {
    if (!reader) { setMyFollows([]); return; }
    fetch(`${API_URL}/api/reader/follows`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setMyFollows(d.following || []))
      .catch(() => {});
  }, [reader]);

  const toggleFollow = async (target) => {
    if (!reader) { startReaderSignIn(); return; }
    const following = !myFollows.includes(target);
    try {
      const res = await fetch(`${API_URL}/api/reader/follow`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target, following }) });
      if (!res.ok) throw new Error();
      setMyFollows((c) => (following ? [...c, target] : c.filter((t) => t !== target)));
      fetchFollowCounts();
      toast.success(following
        ? (target === "author" ? "Following Shailla Khan. New essays will find you." : "Following Mind & Mizan. New essays will find you.")
        : "Unfollowed. No more new-essay letters for this.");
    } catch {
      toast.error("Could not update your follow right now.");
    }
  };

  const followBtnCls = (active) =>
    `px-4 py-2 rounded-full text-xs font-medium transition-all inline-flex items-center gap-1.5 ${active ? "bg-[#B85D34] text-white shadow-sm" : darkMode ? "bg-[#181C22] border border-[#2A2F35] hover:border-[#B85D34]" : "bg-[#F4EFE6] border border-[#E6DEC7] hover:border-[#B85D34]"}`;

  const renderFollowRow = () => (
    <div data-testid="follow-row" className="flex flex-wrap items-center justify-center gap-3">
      <span className="text-[10px] uppercase tracking-[0.2em] opacity-50">Follow along</span>
      <button data-testid="follow-author-btn" aria-pressed={myFollows.includes("author")} onClick={() => toggleFollow("author")} className={followBtnCls(myFollows.includes("author"))}>
        {myFollows.includes("author") && <Check size={13} />} {myFollows.includes("author") ? "Following Shailla Khan" : "Follow Shailla Khan"} <span className="opacity-60">· {followCounts.author}</span>
      </button>
      <button data-testid="follow-publication-btn" aria-pressed={myFollows.includes("publication")} onClick={() => toggleFollow("publication")} className={followBtnCls(myFollows.includes("publication"))}>
        {myFollows.includes("publication") && <Check size={13} />} {myFollows.includes("publication") ? "Following Mind & Mizan" : "Follow Mind & Mizan"} <span className="opacity-60">· {followCounts.publication}</span>
      </button>
    </div>
  );

  // Deep-link support: open ?essay=<id> and handle browser back/forward
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const essayFromUrl = () => new URLSearchParams(window.location.search).get("essay");
    const initial = essayFromUrl();
    if (initial) handleOpenArticle(initial, { replace: true });
    const onPop = () => {
      const id = essayFromUrl();
      if (id) handleOpenArticle(id, { replace: true });
      else { setCurrentArticle(null); setActiveTab("home"); updatePageMeta(null); }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const [siteTexts, setSiteTexts] = useState(DEFAULT_SITE_TEXTS);
  const [sacredEntries, setSacredEntries] = useState([]);
  const [sacredKind, setSacredKind] = useState("all");
  const [books, setBooks] = useState([]);
  const [currentBook, setCurrentBook] = useState(null);
  const [aboutSection, setAboutSection] = useState("publication");
  const [womenTag, setWomenTag] = useState(null);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  const fetchSacred = () => {
    fetch(`${API_URL}/api/sacred-texts`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setSacredEntries(d.entries || []))
      .catch(() => {});
  };

  const fetchBooks = () => {
    fetch(`${API_URL}/api/books`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setBooks(d.books || []))
      .catch(() => {});
  };

  const fetchSeries = () => {
    fetch(`${API_URL}/api/series`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setSeriesData(d.series || []))
      .catch(() => {});
  };

  const fetchSiteTexts = () => {
    fetch(`${API_URL}/api/site-texts`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (d.texts) setSiteTexts(d.texts); })
      .catch(() => {});
  };

  // Fetch named series arcs + author-editable site words + sacred texts + books
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSeries(); fetchSiteTexts(); fetchSacred(); fetchBooks(); }, []);

  // Refresh public sections when navigated to (picks up desk edits)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === "sacred") fetchSacred();
    if (activeTab === "books") fetchBooks();
  }, [activeTab]);

  const scrollTop = () => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { duration: 1 });
    else window.scrollTo({ top: 0 });
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/articles?`;
      if (selectedTopic !== "All") url += `topic=${encodeURIComponent(selectedTopic)}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      if (selectedTag) url += `tag=${encodeURIComponent(selectedTag)}&`;

      const res = await fetch(url);
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (err) {
      console.error("Failed to fetch articles", err);
      toast.error("Could not load publication articles from server.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenArticle = async (articleId, { replace = false } = {}) => {
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}`);
      if (!res.ok) throw new Error("Article not found");
      const data = await res.json();
      setCurrentArticle(data);
      setActiveTab("article-detail");
      updatePageMeta(data);
      const url = `${window.location.pathname}?essay=${articleId}`;
      if (replace) window.history.replaceState({}, "", url);
      else window.history.pushState({}, "", url);
      scrollTop();
      if (reader) {
        fetch(`${API_URL}/api/reader/history`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ article_id: articleId }) }).catch(() => {});
        setReadIds((prev) => (prev.includes(articleId) ? prev : [...prev, articleId]));
      }
    } catch (err) {
      toast.error("Unable to open article.");
    }
  };

  const leaveArticle = (target = "articles") => {
    setCurrentArticle(null);
    setActiveTab(target);
    updatePageMeta(null);
    window.history.pushState({}, "", window.location.pathname);
  };

  const handleLike = async (articleId, e) => {
    e?.stopPropagation();
    try {
      if (reader) {
        const liked = !likedIds.includes(articleId);
        const res = await fetch(`${API_URL}/api/reader/like`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ article_id: articleId, liked })
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error();
        const nextLikes = data.liked ? [...likedIds, articleId] : likedIds.filter((id) => id !== articleId);
        setLikedIds(nextLikes);
        setArticles((prev) => prev.map((a) => (a.id === articleId ? { ...a, likes: data.likes } : a)));
        if (currentArticle && currentArticle.id === articleId) setCurrentArticle((prev) => ({ ...prev, likes: data.likes }));
        toast.success(data.liked ? "Thank you, the author will know this one stayed with someone." : "Reflection withdrawn.");
        return;
      }
      const res = await fetch(`${API_URL}/api/articles/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ article_id: articleId, bookmarked: !bookmarkedIds.includes(articleId) })
      });
      const data = await res.json();
      if (data.success) {
        const nextLikes = [...likedIds, articleId];
        setLikedIds(nextLikes);
        saveGuestState(bookmarkedIds, nextLikes);
        setArticles(prev => prev.map(a => a.id === articleId ? { ...a, likes: data.likes } : a));
        if (currentArticle && currentArticle.id === articleId) {
          setCurrentArticle(prev => ({ ...prev, likes: data.likes }));
        }
        toast.success("Thank you, the author will know this one stayed with someone.");
      }
    } catch (err) {
      toast.error("Failed to record resonance.");
    }
  };

  const handleBookmark = async (articleId, e) => {
    e?.stopPropagation();
    try {
      if (reader) {
        const bookmarked = !bookmarkedIds.includes(articleId);
        const res = await fetch(`${API_URL}/api/reader/bookmark`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ article_id: articleId, bookmarked })
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error();
        const nextBookmarks = data.bookmarked ? [...bookmarkedIds, articleId] : bookmarkedIds.filter((id) => id !== articleId);
        setBookmarkedIds(nextBookmarks);
        setArticles((prev) => prev.map((a) => (a.id === articleId ? { ...a, bookmarks: data.bookmarks } : a)));
        if (currentArticle && currentArticle.id === articleId) setCurrentArticle((prev) => ({ ...prev, bookmarks: data.bookmarks }));
        if (data.bookmarked) toast.success("Article saved to your sanctuary, on every device.");
        else toast.info("Article removed from sanctuary.");
        return;
      }
      const res = await fetch(`${API_URL}/api/articles/bookmark`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ article_id: articleId })
      });
      const data = await res.json();
      if (data.success) {
        let nextBookmarks;
        if (data.bookmarked) {
          nextBookmarks = [...bookmarkedIds, articleId];
          toast.success("Article saved to your personal sanctuary.");
        } else {
          nextBookmarks = bookmarkedIds.filter(id => id !== articleId);
          toast.info("Article removed from sanctuary.");
        }
        setBookmarkedIds(nextBookmarks);
        saveGuestState(nextBookmarks, likedIds);
        setArticles(prev => prev.map(a => a.id === articleId ? { ...a, bookmarks: data.bookmarks } : a));
        if (currentArticle && currentArticle.id === articleId) {
          setCurrentArticle(prev => ({ ...prev, bookmarks: data.bookmarks }));
        }
      }
    } catch (err) {
      toast.error("Failed to update bookmark.");
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!emailInput) return;
    setIsSubmittingNews(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailInput })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.8 } });
        setEmailInput("");
      } else {
        toast.error("Subscription failed.");
      }
    } catch (err) {
      toast.error("Network error during newsletter subscription.");
    } finally {
      setIsSubmittingNews(false);
    }
  };

  const topics = ["All", "The Mind", "Qur'anic Reflection", "Psychology & Neuroscience", "Character & Life"];
  const allTags = ["Neuroscience", "Silence", "Tafakkur", "Mental Health", "Qur'an", "Psychology", "Heart", "Emotional Regulation", "Neuroplasticity", "Tawbah", "Sabr", "Gratitude"];

  const seriesList = articles.filter((a) => a.series).sort((a, b) => (a.series_order || 0) - (b.series_order || 0));
  const [seriesData, setSeriesData] = useState([]);
  const nextInSeries = currentArticle?.series
    ? seriesList.filter((a) => a.series === currentArticle.series).find((a) => a.series_order === (currentArticle.series_order || 0) + 1) || seriesList.filter((a) => a.series === currentArticle.series)[0] || null
    : null;
  const isSeriesEnd = currentArticle?.series && !seriesList.filter((a) => a.series === currentArticle.series).find((a) => a.series_order === (currentArticle.series_order || 0) + 1);
  const seriesCount = (name) => seriesList.filter((a) => a.series === name).length;
  const currentArcIndex = currentArticle?.series ? seriesData.findIndex((s) => s.name === currentArticle.series) : -1;
  const nextArc = currentArcIndex >= 0 && currentArcIndex < seriesData.length - 1 ? seriesData[currentArcIndex + 1] : null;

  const cardCls = `group cursor-pointer rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col justify-between`;

  const renderArticleCard = (art, prefix) => (
    <div
      key={art.id}
      data-testid={`article-card-${art.id}`}
      onClick={() => handleOpenArticle(art.id)}
      className={`${cardCls} ${darkMode ? "bg-[#181C22] border-[#2A2F35] hover:border-[#B85D34]/50" : "bg-[#F4EFE6] border-[#E6DEC7] hover:border-[#B85D34]/50"}`}
    >
      {art.cover && (
        <div className="overflow-hidden rounded-xl mb-5 -mt-1 aspect-[16/10]">
          <img src={mediaSrc(art.cover)} alt={`${art.title} cover`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
        </div>
      )}
      <div className="space-y-4">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold uppercase tracking-wider text-[#B85D34]">{art.topic}</span>
          <span className="opacity-60 flex items-center gap-2">
            {readIds.includes(art.id) && <span data-testid={`read-badge-${art.id}`} className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-semibold uppercase tracking-wider"><Check size={12} /> Read</span>}
            {art.readTime}
          </span>
        </div>
        <h3 className="font-serif text-xl font-medium leading-snug group-hover:text-[#B85D34] transition-colors">
          {art.title}
        </h3>
        <p className="text-sm opacity-85 line-clamp-3 font-light leading-relaxed">
          {art.excerpt}
        </p>
      </div>

      <div className="pt-6 mt-6 border-t border-[#E6DEC7]/60 dark:border-[#2A2F35] flex items-center justify-between text-xs opacity-80">
        <span>{art.date}</span>
        <div className="flex items-center gap-3">
          <button
            data-testid={`${prefix}bookmark-btn-${art.id}`}
            onClick={(e) => handleBookmark(art.id, e)}
            aria-label="Save to sanctuary"
            className="flex items-center gap-1 hover:text-[#B85D34] transition-colors"
          >
            <Bookmark size={14} className={bookmarkedIds.includes(art.id) ? "fill-[#B85D34] text-[#B85D34]" : ""} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen transition-colors duration-500 ${darkMode ? "bg-[#121417] text-[#EDE8DF]" : "bg-[#FBF9F5] text-[#1F2328]"} font-sans selection:bg-[#B85D34]/20 selection:text-[#B85D34]`}>
      <div className="grain-overlay" aria-hidden="true" />
      <Toaster position="bottom-right" richColors />

      {/* NAVIGATION BAR */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl ${darkMode ? "bg-[#121417]/80 border-b border-[#2A2F35]" : "bg-[#FBF9F5]/85 border-b border-[#E6DEC7]"} px-6 lg:px-16 py-4 flex items-center justify-between transition-colors`}>
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setActiveTab("home"); setSelectedTag(null); setSearchQuery(""); window.history.pushState({}, "", window.location.pathname); }}>
          <div className="w-10 h-10 rounded-full bg-[#B85D34] text-white flex items-center justify-center font-serif text-xl shadow-md">
            M
          </div>
          <div>
            <h1 className="font-serif text-xl lg:text-2xl font-bold tracking-tight">Mind & Mizan</h1>
            <p className="text-xs uppercase tracking-[0.2em] opacity-60 font-medium">by {AUTHOR.name}</p>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6 font-medium text-sm">
          {[
            { tab: "home", label: "Home", testid: "nav-home", onClick: () => { setActiveTab("home"); setSelectedTag(null); setSearchQuery(""); window.history.pushState({}, "", window.location.pathname); } },
            { tab: "articles", label: "Essays", testid: "nav-articles", onClick: () => { setActiveTab("articles"); setSelectedTag(null); window.history.pushState({}, "", window.location.pathname); } },
            { tab: "sacred", label: "Qur'an & Hadith", testid: "nav-sacred" },
            { tab: "women", label: "Women", testid: "nav-women" },
            { tab: "books", label: "Books", testid: "nav-books" },
            { tab: "about", label: "About", testid: "nav-about" },
          ].map((item) => (
            <button
              key={item.tab}
              data-testid={item.testid}
              onClick={item.onClick || (() => setActiveTab(item.tab))}
              className={`editorial-link transition-colors hover:text-[#B85D34] ${activeTab === item.tab ? "text-[#B85D34] font-semibold" : "opacity-75"}`}
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="nav-bookmarks"
            onClick={() => setActiveTab("bookmarks")}
            className={`editorial-link transition-colors hover:text-[#B85D34] flex items-center gap-1.5 ${activeTab === "bookmarks" ? "text-[#B85D34] font-semibold" : "opacity-75"}`}
          >
            <Bookmark size={15} /> Sanctuary ({bookmarkedIds.length})
          </button>
          <button data-testid="nav-author-desk" onClick={() => setActiveTab("author")} className={`editorial-link transition-colors hover:text-[#B85D34] ${activeTab === "author" ? "text-[#B85D34] font-semibold" : "opacity-75"}`}><PenLine size={15} className="inline mr-1" /> Author Desk</button>
        </nav>

        <div className="flex items-center gap-3">
          {reader ? (
            <div className="flex items-center gap-2">
            <button data-testid="reader-library-btn" onClick={() => setActiveTab("library")} title="Open your personal library" className="flex items-center gap-2 cursor-pointer">
              {reader.picture ? (
                <img data-testid="reader-avatar" src={reader.picture} alt={reader.name} className="w-9 h-9 rounded-full border border-[#B85D34]/40 object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div data-testid="reader-avatar" className="w-9 h-9 rounded-full bg-[#B85D34]/15 border border-[#B85D34]/40 flex items-center justify-center font-serif text-sm text-[#B85D34]">
                  {(reader.name || "R").slice(0, 1).toUpperCase()}
                </div>
              )}
            </button>
              <button data-testid="reader-signout-btn" onClick={readerSignOut} title={`Signed in as ${reader.email}, sign out`} className={`hidden sm:flex items-center gap-1.5 text-xs opacity-70 hover:text-[#B85D34] transition-colors`}>
                <LogOut size={13} /> Sign out
              </button>
            </div>
          ) : (
            <button
              data-testid="reader-signin-btn"
              onClick={startReaderSignIn}
              title="Sign in with Google to keep your sanctuary everywhere"
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-xs font-medium transition-all hover:border-[#B85D34] hover:text-[#B85D34] ${darkMode ? "border-[#3A4048] bg-[#1E232A]" : "border-[#E6DEC7] bg-[#F4EFE6]"}`}
            >
              <GoogleLogo size={15} weight="bold" /> <span className="hidden sm:inline">Reader sign-in</span>
            </button>
          )}
          <button
            data-testid="theme-toggle"
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2.5 rounded-full border transition-all ${darkMode ? "border-[#3A4048] bg-[#1E232A] text-amber-400" : "border-[#E6DEC7] bg-[#F4EFE6] text-amber-800"}`}
            title="Toggle theme"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <a
            data-testid="header-newsletter-cta"
            href="#newsletter"
            onClick={(e) => { e.preventDefault(); document.getElementById('newsletter-section')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B85D34] text-white text-sm font-medium hover:bg-[#A04E29] transition-all shadow-sm"
          >
            <Mail size={15} /> Get Dispatch
          </a>
        </div>
      </header>

      {/* MOBILE NAV BAR */}
      <div className={`lg:hidden border-b ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
        <div className="flex items-center justify-around py-3 text-xs">
          <button data-testid="mobile-nav-home" onClick={() => { setActiveTab("home"); setSelectedTag(null); setMobileMoreOpen(false); }} className={`flex flex-col items-center gap-1 ${activeTab === "home" ? "text-[#B85D34] font-bold" : "opacity-70"}`}>
            <BookOpen size={18} /> Home
          </button>
          <button data-testid="mobile-nav-articles" onClick={() => { setActiveTab("articles"); setSelectedTag(null); setMobileMoreOpen(false); }} className={`flex flex-col items-center gap-1 ${activeTab === "articles" ? "text-[#B85D34] font-bold" : "opacity-70"}`}>
            <Compass size={18} /> Essays
          </button>
          <button data-testid="mobile-nav-bookmarks" onClick={() => { setActiveTab("bookmarks"); setMobileMoreOpen(false); }} className={`flex flex-col items-center gap-1 ${activeTab === "bookmarks" ? "text-[#B85D34] font-bold" : "opacity-70"}`}>
            <Bookmark size={18} /> Sanctuary
          </button>
          <button data-testid="mobile-nav-more" onClick={() => setMobileMoreOpen(!mobileMoreOpen)} className={`flex flex-col items-center gap-1 ${mobileMoreOpen ? "text-[#B85D34] font-bold" : "opacity-70"}`}>
            {mobileMoreOpen ? <X size={18} /> : <Menu size={18} />} More
          </button>
        </div>
        {mobileMoreOpen && (
          <div data-testid="mobile-more-menu" className="grid grid-cols-2 gap-1 px-4 pb-4 text-sm">
            {[
              { tab: "sacred", label: "Qur'an & Hadith", icon: ScrollText, testid: "mobile-nav-sacred" },
              { tab: "women", label: "Women", icon: Flower2, testid: "mobile-nav-women" },
              { tab: "books", label: "Books", icon: LibraryBig, testid: "mobile-nav-books" },
              { tab: "about", label: "About", icon: Info, testid: "mobile-nav-about" },
              { tab: "author", label: "Author Desk", icon: PenLine, testid: "mobile-nav-author" },
            ].map((item) => (
              <button key={item.tab} data-testid={item.testid} onClick={() => { setActiveTab(item.tab); setMobileMoreOpen(false); }} className={`flex items-center gap-2.5 rounded-xl px-3 py-3 text-left transition-colors ${activeTab === item.tab ? "bg-[#B85D34]/10 text-[#B85D34] font-semibold" : "hover:bg-[#B85D34]/5"}`}>
                <item.icon size={16} /> {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-12">

        {/* VIEW 1: HOME */}
        {activeTab === "home" && (
          <div className="space-y-24">
            {/* HERO SECTION */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-4 lg:pt-10">
              <motion.div className="lg:col-span-7 space-y-8" style={{ y: heroTextY }}>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-[0.15em] uppercase bg-[#B85D34]/10 text-[#B85D34]"
                >
                  <Sparkles size={13} /> {siteTexts.hero.kicker}
                </motion.div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal leading-[1.12] tracking-tight">
                  <MaskedLine delay={0.15}>{siteTexts.hero.line1}</MaskedLine>
                  <MaskedLine delay={0.32}>meets <em className="text-[#B85D34] not-italic border-b-2 border-[#B85D34]/40">{siteTexts.hero.highlight}</em>.</MaskedLine>
                </h1>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                  className="text-base md:text-lg opacity-80 leading-relaxed font-light max-w-xl"
                >
                  {siteTexts.hero.intro}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                  className="flex flex-wrap items-center gap-4 pt-2"
                >
                  <button
                    data-testid="hero-explore-essays"
                    onClick={() => setActiveTab("articles")}
                    className="group px-8 py-3.5 rounded-full bg-[#1F2328] text-white dark:bg-[#EDE8DF] dark:text-[#121417] text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2 shadow-md"
                  >
                    Explore Essays <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    data-testid="hero-author-phil"
                    onClick={() => { setActiveTab("about"); setAboutSection("philosophy"); }}
                    className={`px-8 py-3.5 rounded-full border text-sm font-medium transition-all ${darkMode ? "border-[#3A4048] hover:bg-[#1E232A]" : "border-[#E6DEC7] hover:bg-[#F4EFE6]"}`}
                  >
                    Read Author's Note
                  </button>
                </motion.div>
              </motion.div>

              <motion.div
                className="lg:col-span-5 relative"
                initial={{ opacity: 0, scale: 0.96, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
              >
                <div className={`p-4 rounded-2xl border shadow-xl ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                  <div className="overflow-hidden rounded-xl aspect-[4/5] relative">
                    <motion.img
                      style={{ y: heroImageY, scale: 1.12 }}
                      src="https://images.unsplash.com/photo-1508153460964-48ffffcb0829"
                      alt="Contemplative silhouette"
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-6 text-white">
                      <span className="text-xs uppercase tracking-widest text-[#D9822B] font-semibold mb-1">Featured Essay</span>
                      <h3 className="font-serif text-xl font-medium leading-snug">The Architecture of Silence: Finding Equilibrium in a Noisy Age</h3>
                      <p className="text-xs opacity-80 mt-2">{AUTHOR.name} • 7 min read</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </section>

            {/* EDITORIAL MARQUEE */}
            <div className={`relative overflow-hidden border-y py-5 -mx-6 lg:-mx-12 px-6 ${darkMode ? "border-[#2A2F35]" : "border-[#E6DEC7]"}`} aria-hidden="true">
              <div className="marquee-track gap-10">
                {[...MARQUEE_WORDS, ...MARQUEE_WORDS].map((word, i) => (
                  <span key={i} className="flex items-center gap-10 font-serif text-2xl lg:text-3xl opacity-40 whitespace-nowrap italic">
                    {word} <span className="text-[#B85D34] not-italic text-lg">✦</span>
                  </span>
                ))}
              </div>
            </div>

            {/* THE INNER LIFE, SERIES BANNER */}
            {seriesList.length > 0 && (
              <Reveal>
                <section data-testid="home-series-banner" className={`rounded-3xl border p-8 lg:p-12 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
                    <div className="space-y-3 max-w-xl">
                      <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">A series in five movements</span>
                      <h2 className="font-serif text-3xl lg:text-4xl font-normal">The Inner Life</h2>
                      <p className="text-sm opacity-75 font-light leading-relaxed">The scale within. The stillness that descends. The anxious heart. The envious heart. Made on purpose, five essays on the heart's measure, best read in order, with tea.</p>
                    </div>
                    <button data-testid="begin-series-btn" onClick={() => setActiveTab("series")} className="group self-start lg:self-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#B85D34] text-white text-sm font-medium hover:bg-[#A04E29] transition-all shrink-0">
                      Begin the journey <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {seriesList.filter((a) => a.series === "The Inner Life").map((art) => (
                      <button key={art.id} data-testid={`home-series-${art.id}`} onClick={() => handleOpenArticle(art.id)} className="group text-left space-y-3">
                        <div className="overflow-hidden rounded-xl aspect-[4/3] relative">
                          {art.cover && <img src={mediaSrc(art.cover)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />}
                          <span className="absolute top-2 left-2 font-serif italic text-sm bg-[#FBF9F5]/90 text-[#B85D34] rounded-full w-7 h-7 flex items-center justify-center shadow-sm">{art.series_order}</span>
                        </div>
                        <h4 className="font-serif text-base font-medium leading-snug group-hover:text-[#B85D34] transition-colors line-clamp-2">{art.title}</h4>
                      </button>
                    ))}
                  </div>
                </section>
              </Reveal>
            )}

            {/* TOPIC NAVIGATION PILLS */}
            <Reveal>
              <section className="space-y-6">
                <div className="flex items-center justify-between border-b pb-4 border-[#E6DEC7] dark:border-[#2A2F35]">
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif text-sm text-[#B85D34] italic">01</span>
                    <h2 className="font-serif text-2xl font-normal">Explore by Topic</h2>
                  </div>
                  <button onClick={() => { setSelectedTopic("All"); setActiveTab("articles"); }} className="text-xs uppercase tracking-wider text-[#B85D34] font-semibold editorial-link">
                    View All ({articles.length})
                  </button>
                </div>

                <div className="flex flex-wrap gap-3">
                  {topics.map(topic => (
                    <button
                      key={topic}
                      data-testid={`topic-pill-${topic.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      onClick={() => { setSelectedTopic(topic); setActiveTab("articles"); }}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${selectedTopic === topic && activeTab === "articles" ? "bg-[#B85D34] text-white shadow-sm" : darkMode ? "bg-[#181C22] border border-[#2A2F35] text-[#EDE8DF] hover:border-[#B85D34]" : "bg-[#F4EFE6] border border-[#E6DEC7] text-[#1F2328] hover:border-[#B85D34]"}`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </section>
            </Reveal>

            {/* LATEST ESSAYS SECTION */}
            <Reveal>
              <section className="space-y-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif text-sm text-[#B85D34] italic">02</span>
                    <div>
                      <h2 className="font-serif text-3xl font-normal">Latest Publications</h2>
                      <p className="text-sm opacity-60 mt-1">Carefully crafted reflections on mind, spirit, and character.</p>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="py-20 text-center opacity-60 animate-pulse">Loading contemplative reflections...</div>
                ) : articles.length === 0 ? (
                  <div className="py-20 text-center opacity-60">No essays found matching your criteria. Try resetting filters.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {articles.slice(0, 6).map((art, i) => (
                      <motion.div
                        key={art.id}
                        initial={{ opacity: 0, y: 28 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.65, delay: i * 0.08, ease: EASE }}
                      >
                        {renderArticleCard(art, "")}
                      </motion.div>
                    ))}
                  </div>
                )}
              </section>
            </Reveal>

            {/* AUTHOR PHILOSOPHY PREVIEW BANNER */}
            <Reveal>
              <section className={`rounded-3xl p-8 lg:p-12 border relative overflow-hidden ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                <div className="absolute right-0 top-0 bottom-0 w-1/3 hidden lg:block opacity-20 pointer-events-none">
                  <img src="https://images.unsplash.com/photo-1699465301322-362016624dd7" alt="Abstract" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-2xl space-y-6 relative z-10">
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif text-sm text-[#B85D34] italic">03</span>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Personal Author Philosophy</span>
                  </div>
                  <h2 className="font-serif text-3xl font-normal leading-snug">
                    "{siteTexts.manifesto.banner_quote}"
                  </h2>
                  <p className="opacity-85 font-light leading-relaxed text-sm">
                    {siteTexts.manifesto.banner_subtext}
                  </p>
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="read-full-philosophy-btn"
                      onClick={() => { setActiveTab("about"); setAboutSection("philosophy"); }}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#B85D34] editorial-link"
                    >
                      Read full philosophy statement <ArrowRight size={16} />
                    </button>
                    <span className="text-xs opacity-60 font-serif italic">{AUTHOR.name}</span>
                  </div>
                </div>
              </section>
            </Reveal>

            {/* NEWSLETTER SECTION */}
            <Reveal>
              <section id="newsletter-section" className={`rounded-3xl p-8 lg:p-16 border text-center space-y-6 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                <div className="max-w-xl mx-auto space-y-4">
                  <div className="flex items-baseline justify-center gap-4">
                    <span className="font-serif text-sm text-[#B85D34] italic">04</span>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">The Mizan Dispatch</span>
                  </div>
                  <h2 className="font-serif text-3xl lg:text-4xl font-normal">Receive weekly reflections in your inbox.</h2>
                  <p className="text-sm opacity-85 font-light">
                    No noise, no spam. Just one thoughtful essay and curated reading recommendations every Friday morning.
                  </p>
                </div>

                <form onSubmit={handleSubscribe} className="max-w-md mx-auto flex flex-col sm:flex-row gap-3 pt-2">
                  <input
                    data-testid="newsletter-email-input"
                    type="email"
                    required
                    placeholder="Enter your email address..."
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    className={`flex-1 px-5 py-3.5 rounded-full border text-sm outline-none transition-all ${darkMode ? "bg-[#121417] border-[#3A4048] text-white focus:border-[#B85D34]" : "bg-white border-[#E6DEC7] text-[#1F2328] focus:border-[#B85D34]"}`}
                  />
                  <button
                    data-testid="newsletter-submit-btn"
                    type="submit"
                    disabled={isSubmittingNews}
                    className="px-8 py-3.5 rounded-full bg-[#B85D34] text-white text-sm font-medium hover:bg-[#A04E29] transition-all shadow-md disabled:opacity-50"
                  >
                    {isSubmittingNews ? "Joining..." : "Join Circle"}
                  </button>
                </form>
              </section>
            </Reveal>
          </div>
        )}

        {/* VIEW 2: ALL ARTICLES & SEARCH / TAGS */}
        {activeTab === "articles" && (
          <div className="space-y-12 animate-fade-in">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Publication Archive</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">Essays & Reflections</h1>
              <p className="opacity-85 max-w-2xl font-light">
                Browse the complete repository of essays on the human mind, Qur'anic reflection, psychology, and character.
              </p>
              <button data-testid="browse-series-btn" onClick={() => setActiveTab("series")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#B85D34] editorial-link">
                Browse by series <ArrowRight size={15} />
              </button>
            </div>

            {/* SEARCH AND FILTERS */}
            <div className="space-y-6">
              <div className="relative">
                <Search className="absolute left-4 top-3.5 opacity-50" size={18} />
                <input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search articles by title, topic, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-12 pr-4 py-3.5 rounded-2xl border text-sm outline-none transition-all ${darkMode ? "bg-[#181C22] border-[#2A2F35] text-white focus:border-[#B85D34]" : "bg-[#F4EFE6] border-[#E6DEC7] text-[#1F2328] focus:border-[#B85D34]"}`}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {topics.map(topic => (
                    <button
                      key={topic}
                      data-testid={`filter-topic-${topic.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      onClick={() => setSelectedTopic(topic)}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${selectedTopic === topic ? "bg-[#B85D34] text-white" : darkMode ? "bg-[#181C22] border border-[#2A2F35] text-[#EDE8DF]" : "bg-[#F4EFE6] border border-[#E6DEC7] text-[#1F2328]"}`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>

                {selectedTag && (
                  <div className="flex items-center gap-2 text-xs bg-[#B85D34]/10 text-[#B85D34] px-3 py-1.5 rounded-full font-medium">
                    <TagIcon size={13} /> Tag: {selectedTag}
                    <button data-testid="clear-tag-filter" onClick={() => setSelectedTag(null)} className="ml-1 font-bold hover:opacity-75">×</button>
                  </div>
                )}
              </div>

              {/* TAG CLOUD */}
              <div className="flex flex-wrap gap-2 pt-2 items-center">
                <span className="text-xs uppercase tracking-wider opacity-60 mr-2">Tags:</span>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    data-testid={`tag-pill-${tag.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    className={`px-3 py-1 rounded-lg text-xs transition-colors ${selectedTag === tag ? "bg-[#B85D34] text-white font-semibold" : darkMode ? "bg-[#181C22] hover:bg-[#2A2F35] opacity-75" : "bg-[#F4EFE6] hover:bg-[#E6DEC7] opacity-80"}`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>

            {/* RESULTS GRID */}
            {loading ? (
              <div className="py-20 text-center opacity-60 animate-pulse">Filtering essays...</div>
            ) : articles.length === 0 ? (
              <div className="py-20 text-center space-y-4">
                <p className="opacity-60">No essays matched your search query or filters.</p>
                <button
                  onClick={() => { setSelectedTopic("All"); setSearchQuery(""); setSelectedTag(null); }}
                  className="px-6 py-2.5 rounded-full bg-[#B85D34] text-white text-xs font-medium"
                >
                  Reset All Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((art, i) => (
                  <motion.div
                    key={art.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.55, delay: (i % 3) * 0.07, ease: EASE }}
                  >
                    {renderArticleCard(art, "archive-")}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VIEW 3: ARTICLE DETAIL READING VIEW */}
        {activeTab === "article-detail" && currentArticle && (
          <div className="max-w-3xl mx-auto space-y-10 animate-fade-in pb-20">
            {/* Top Back Navigation & Reader Controls */}
            <div className="flex items-center justify-between border-b pb-4 border-[#E6DEC7] dark:border-[#2A2F35]">
              <button
                data-testid="back-to-articles-btn"
                onClick={() => leaveArticle("articles")}
                className="text-xs uppercase tracking-widest font-semibold text-[#B85D34] editorial-link flex items-center gap-2"
              >
                ← Back to Essays
              </button>

              <div className="flex items-center gap-4 text-xs">
                <span className="opacity-65">Font Size:</span>
                <button data-testid="reset-filters-btn"
                  onClick={() => setFontSize("normal")}
                  className={`px-2.5 py-1 rounded border ${fontSize === "normal" ? "bg-[#B85D34] text-white border-[#B85D34]" : "opacity-75"}`}
                >
                  A
                </button>
                <button
                  onClick={() => setFontSize("large")}
                  className={`px-2.5 py-1 rounded border text-sm ${fontSize === "large" ? "bg-[#B85D34] text-white border-[#B85D34]" : "opacity-75"}`}
                >
                  A+
                </button>
                <button
                  onClick={() => setFontSize("xlarge")}
                  className={`px-2.5 py-1 rounded border text-base ${fontSize === "xlarge" ? "bg-[#B85D34] text-white border-[#B85D34]" : "opacity-75"}`}
                >
                  A++
                </button>
              </div>
            </div>

            {/* Article Header */}
            <div className="space-y-6 text-center lg:text-left">
              {currentArticle.cover && (
                <div className="overflow-hidden rounded-2xl aspect-[21/9] border border-[#E6DEC7] dark:border-[#2A2F35]">
                  <img src={mediaSrc(currentArticle.cover)} alt={`${currentArticle.title} cover`} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 text-xs uppercase tracking-widest font-semibold text-[#B85D34]">
                <span>{currentArticle.topic}</span>
                <span>•</span>
                <span>{currentArticle.readTime}</span>
                {currentArticle.series && (
                  <button data-testid="series-badge" onClick={() => leaveArticle("series")} className={`px-3 py-1 rounded-full border transition-colors hover:border-[#B85D34] ${darkMode ? "border-[#3A4048]" : "border-[#E6DEC7]"} text-[#B85D34]`}>
                    {currentArticle.series} · Essay {currentArticle.series_order} of {seriesCount(currentArticle.series)}
                  </button>
                )}
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal leading-tight tracking-tight">
                {currentArticle.title}
              </h1>
              <p className="text-base md:text-lg opacity-80 font-light italic">
                {currentArticle.excerpt}
              </p>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-[#E6DEC7] dark:border-[#2A2F35] text-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${darkMode ? "border-[#B85D34]/40 bg-[#B85D34]/15" : "border-[#B85D34]/40 bg-[#B85D34]/10"}`}>
                    <span className="font-serif text-sm text-[#B85D34] font-semibold">{AUTHOR.initials}</span>
                  </div>
                  <div>
                    <span className="font-medium block">{currentArticle.author}</span>
                    <span className="opacity-65 block text-xs">{currentArticle.date}</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    data-testid="stayed-btn"
                    onClick={(e) => handleLike(currentArticle.id, e)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs font-medium transition-all ${likedIds.includes(currentArticle.id) ? "bg-[#B85D34] text-white border-[#B85D34]" : "border-[#E6DEC7] dark:border-[#3A4048] hover:border-[#B85D34] hover:text-[#B85D34]"}`}
                  >
                    <Heart size={14} className={likedIds.includes(currentArticle.id) ? "fill-white" : ""} />
                    <span>{likedIds.includes(currentArticle.id) ? "Stayed with you" : "This stayed with me"}</span>
                  </button>
                  <button
                    data-testid="detail-bookmark-btn"
                    onClick={(e) => handleBookmark(currentArticle.id, e)}
                    aria-label="Save to sanctuary"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E6DEC7] dark:border-[#3A4048] hover:border-[#B85D34] transition-colors"
                  >
                    <Bookmark size={15} className={bookmarkedIds.includes(currentArticle.id) ? "fill-[#B85D34] text-[#B85D34]" : ""} />
                  </button>
                </div>
              </div>

              {/* Share Bar */}
              <div className={`pt-4`}>
                <ShareBar article={currentArticle} darkMode={darkMode} />
              </div>
            </div>

            {/* Narrated audio reflection (real TTS, generated by the author) */}
            {currentArticle.audio?.url && (
              <AudioPlayer
                src={mediaSrc(currentArticle.audio.url)}
                title={currentArticle.title}
                meta={`${currentArticle.readTime} essay`}
                darkMode={darkMode}
                articleId={currentArticle.id}
              />
            )}

            {/* Article Content Body (copy-protected on published essays) */}
            <div
              data-testid="article-protected-body"
              className={`space-y-8 font-serif leading-relaxed ${fontSize === "large" ? "text-xl" : fontSize === "xlarge" ? "text-2xl" : "text-lg"} opacity-95 select-none`}
              onContextMenu={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
            >
              {currentArticle.content_html ? (
                <div data-testid="essay-rich-content" className="essay-html" dangerouslySetInnerHTML={{ __html: renderEssayHtml(currentArticle.content_html) }} />
              ) : currentArticle.content.map((paragraph, idx) => {
                const imgMatch = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/.exec((paragraph || "").trim());
                if (imgMatch) return (
                  <figure key={idx} className="py-2">
                    <img data-testid={`essay-inline-image-${idx}`} src={mediaSrc(imgMatch[2])} alt={imgMatch[1] || currentArticle.title} className="w-full rounded-2xl border border-[#E6DEC7] dark:border-[#2A2F35] object-cover" />
                    {imgMatch[1] && <figcaption className="text-center text-sm opacity-60 font-serif italic mt-3">{imgMatch[1]}</figcaption>}
                  </figure>
                );
                return (
                  <p key={idx} className="first-letter:text-5xl first-letter:font-serif first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:text-[#B85D34]">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {/* Tags footer */}
            {(currentArticle.tags || []).length > 0 && (
            <div className="pt-8 border-t border-[#E6DEC7] dark:border-[#2A2F35] flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider opacity-60 mr-2">Tagged with:</span>
              {currentArticle.tags.map(tag => (
                <button data-testid={`detail-tag-${tag.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  key={tag}
                  onClick={() => { leaveArticle("articles"); setSelectedTag(tag); }}
                  className="px-3 py-1 rounded-lg text-xs bg-[#B85D34]/10 text-[#B85D34] font-medium hover:bg-[#B85D34]/20 transition-colors"
                >
                  #{tag}
                </button>
              ))}
            </div>
            )}

            {/* End-of-essay share */}
            <div className={`rounded-2xl border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
              <p className="font-serif text-lg italic opacity-85">If this essay restored a little balance, pass it on.</p>
              <ShareBar article={currentArticle} darkMode={darkMode} />
            </div>

            {/* Quiet follow row */}
            <div className="pt-2 pb-2">{renderFollowRow()}</div>

            {/* Series reading path / arc completion */}
            {nextInSeries && !isSeriesEnd && (
              <button
                data-testid="series-next-card"
                onClick={() => handleOpenArticle(nextInSeries.id)}
                className={`w-full text-left rounded-3xl border p-6 sm:p-8 flex items-center gap-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${darkMode ? "bg-[#181C22] border-[#2A2F35] hover:border-[#B85D34]/50" : "bg-[#F4EFE6] border-[#E6DEC7] hover:border-[#B85D34]/50"}`}
              >
                {nextInSeries.cover && (
                  <img src={mediaSrc(nextInSeries.cover)} alt="" className="hidden sm:block w-24 h-24 rounded-2xl object-cover border border-[#E6DEC7] dark:border-[#2A2F35] shrink-0" />
                )}
                <div className="min-w-0 flex-1 space-y-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">
                    Next in {currentArticle.series}
                  </span>
                  <h3 className="font-serif text-2xl font-medium leading-snug group-hover:text-[#B85D34] transition-colors">{nextInSeries.title}</h3>
                  <p className="text-sm opacity-70 font-light line-clamp-2">{nextInSeries.excerpt}</p>
                </div>
                <ArrowRight size={22} className="text-[#B85D34] shrink-0 group-hover:translate-x-1.5 transition-transform" />
              </button>
            )}

            {/* Arc completion, a quiet closing note, not a celebration */}
            {isSeriesEnd && (
              <div data-testid="arc-completion-card" className={`rounded-3xl border p-8 sm:p-10 text-center space-y-5 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                <span className="text-xs uppercase tracking-[0.25em] text-[#B85D34] font-semibold">End of {currentArticle.series}</span>
                <p className="font-serif text-xl md:text-2xl leading-relaxed opacity-90 max-w-lg mx-auto">
                  You have reached the end of this arc.
                </p>
                <p className="font-serif italic text-base opacity-70 max-w-md mx-auto">
                  Perhaps the questions it opened do not end here.
                </p>
                <div className="pt-2">
                  {nextArc && nextArc.essays?.length > 0 ? (
                    <button
                      data-testid="arc-continue-btn"
                      onClick={() => handleOpenArticle(nextArc.essays[0].id)}
                      className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#B85D34] text-white text-sm font-medium hover:bg-[#A04E29] transition-all"
                    >
                      Continue to {nextArc.name} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : (
                    <button
                      data-testid="arc-continue-btn"
                      onClick={() => { setCurrentArticle(null); setActiveTab("series"); updatePageMeta(null); window.history.pushState({}, "", window.location.pathname); scrollTop(); }}
                      className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#B85D34] text-[#B85D34] text-sm font-medium hover:bg-[#B85D34] hover:text-white transition-all"
                    >
                      See all the series <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  )}
                  <div className="pt-4">
                    <button
                      data-testid="arc-restart-link"
                      onClick={() => handleOpenArticle(nextInSeries.id)}
                      className="text-xs opacity-55 hover:text-[#B85D34] transition-colors editorial-link font-serif italic"
                    >
                      or begin this arc again from its first movement
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* VIEW: QUR'AN & HADITH */}
        {activeTab === "sacred" && (
          <div className="max-w-4xl mx-auto space-y-10 pb-16 animate-fade-in">
            <Reveal className="space-y-4 text-center">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Sacred Texts</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">Qur'an & Hadith</h1>
              <p className="opacity-80 max-w-2xl mx-auto font-light leading-relaxed">
                Verses and narrations gathered with care. The text, the scholarly context, and personal reflection are always labelled and kept apart.
              </p>
            </Reveal>

            <div className="flex justify-center gap-2">
              {[{ id: "all", label: "All" }, { id: "quran", label: "Qur'an" }, { id: "hadith", label: "Hadith" }].map((k) => (
                <button key={k.id} data-testid={`sacred-filter-${k.id}`} onClick={() => setSacredKind(k.id)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${sacredKind === k.id ? "bg-[#B85D34] text-white shadow-sm" : darkMode ? "bg-[#181C22] border border-[#2A2F35] hover:border-[#B85D34]" : "bg-[#F4EFE6] border border-[#E6DEC7] hover:border-[#B85D34]"}`}>{k.label}</button>
              ))}
            </div>

            {sacredEntries.filter((e) => sacredKind === "all" || e.kind === sacredKind).length === 0 ? (
              <div data-testid="sacred-empty" className={`rounded-3xl border border-dashed p-10 text-center ${darkMode ? "border-[#3A4048]" : "border-[#D9CFB8]"}`}>
                <p className="text-sm opacity-50 font-serif italic">The first entries are being gathered with care. They will appear here when they are ready.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {sacredEntries.filter((e) => sacredKind === "all" || e.kind === sacredKind).map((entry) => (
                  <Reveal key={entry.id}>
                    <article data-testid={`sacred-entry-${entry.id}`} className={`rounded-3xl border p-7 lg:p-10 space-y-6 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span data-testid={`sacred-kind-${entry.id}`} className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1 rounded-full ${entry.kind === "quran" ? "bg-[#B85D34] text-white" : "bg-[#1F2328] text-white dark:bg-[#EDE8DF] dark:text-[#121417]"}`}>{entry.kind === "quran" ? "Qur'an" : "Hadith"}</span>
                        {entry.kind === "quran" && entry.reference && <span className="text-xs uppercase tracking-wider text-[#B85D34] font-semibold">{entry.reference}</span>}
                        {entry.kind === "hadith" && entry.collection && <span className="text-xs uppercase tracking-wider text-[#B85D34] font-semibold">{entry.collection}{entry.number ? `, no. ${entry.number}` : ""}</span>}
                      </div>

                      {entry.kind === "quran" ? (
                        <>
                          {entry.arabic && <p dir="rtl" lang="ar" data-testid={`sacred-arabic-${entry.id}`} className="font-serif text-2xl lg:text-3xl leading-loose text-right">{entry.arabic}</p>}
                          {entry.translation && (
                            <div className="space-y-2">
                              <span className="text-[10px] uppercase tracking-[0.18em] opacity-50 font-semibold">Translation</span>
                              <p className="font-serif text-lg lg:text-xl leading-relaxed opacity-95 italic">{entry.translation}</p>
                            </div>
                          )}
                          {entry.tafsir && (
                            <div className="space-y-2">
                              <span className="text-[10px] uppercase tracking-[0.18em] opacity-50 font-semibold">Tafsir & context</span>
                              <p className="text-sm leading-relaxed opacity-85 whitespace-pre-line">{entry.tafsir}</p>
                            </div>
                          )}
                          {entry.asbab && (
                            <div className="space-y-2">
                              <span className="text-[10px] uppercase tracking-[0.18em] opacity-50 font-semibold">Asbab al-Nuzul</span>
                              <p className="text-sm leading-relaxed opacity-85 whitespace-pre-line">{entry.asbab}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {entry.text && <p className="font-serif text-lg lg:text-xl leading-relaxed opacity-95">{entry.text}</p>}
                          <div className="grid sm:grid-cols-2 gap-4 text-sm">
                            {entry.narrator && <div><span className="text-[10px] uppercase tracking-[0.18em] opacity-50 font-semibold block mb-1">Narrator</span><span className="opacity-85">{entry.narrator}</span></div>}
                            {entry.authenticity && <div><span className="text-[10px] uppercase tracking-[0.18em] opacity-50 font-semibold block mb-1">Authenticity</span><span className="opacity-85">{entry.authenticity}</span></div>}
                          </div>
                        </>
                      )}

                      {entry.reflection && (
                        <div className={`rounded-2xl p-5 space-y-2 ${darkMode ? "bg-[#B85D34]/10" : "bg-[#B85D34]/5"}`}>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#B85D34] font-semibold">Personal reflection</span>
                          <p className="font-serif italic text-base leading-relaxed opacity-90 whitespace-pre-line">{entry.reflection}</p>
                        </div>
                      )}

                      {entry.sources && (
                        <div className="pt-4 border-t border-[#E6DEC7] dark:border-[#3A4048] space-y-1">
                          <span className="text-[10px] uppercase tracking-[0.18em] opacity-50 font-semibold">Sources</span>
                          <p className="text-xs opacity-70 leading-relaxed whitespace-pre-line">{entry.sources}</p>
                        </div>
                      )}
                    </article>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VIEW: WOMEN */}
        {activeTab === "women" && (
          <div className="space-y-12 pb-16 animate-fade-in">
            <Reveal className="space-y-4 text-center max-w-3xl mx-auto">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Women</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">Women</h1>
              <p data-testid="women-intro" className="opacity-80 font-light leading-relaxed text-base md:text-lg">
                {siteTexts.women?.intro}
              </p>
            </Reveal>

            <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
              {WOMEN_TOPICS.map((topic) => (
                <button key={topic} data-testid={`women-topic-${topic.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setWomenTag(womenTag === topic ? null : topic)} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${womenTag === topic ? "bg-[#B85D34] text-white" : darkMode ? "bg-[#181C22] border border-[#2A2F35] hover:border-[#B85D34]" : "bg-[#F4EFE6] border border-[#E6DEC7] hover:border-[#B85D34]"}`}>{topic}</button>
              ))}
            </div>

            {(() => {
              const womenEssays = articles.filter((a) => a.section === "women" && (!womenTag || (a.tags || []).includes(womenTag)));
              if (womenEssays.length === 0) return (
                <div data-testid="women-empty" className={`max-w-2xl mx-auto rounded-3xl border border-dashed p-10 text-center ${darkMode ? "border-[#3A4048]" : "border-[#D9CFB8]"}`}>
                  <p className="text-sm opacity-50 font-serif italic">{womenTag ? `No essays under ${womenTag} yet.` : "Essays for this space are being written."} Subscribe to the Mizan Dispatch to know when the first one arrives.</p>
                </div>
              );
              return <div data-testid="women-essays" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">{womenEssays.map((art) => renderArticleCard(art, "women"))}</div>;
            })()}
          </div>
        )}

        {/* VIEW: BOOKS */}
        {activeTab === "books" && (
          <div className="space-y-12 pb-16 animate-fade-in">
            <Reveal className="space-y-4 text-center max-w-3xl mx-auto">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">The Library</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">Books</h1>
              <p className="opacity-80 font-light leading-relaxed">Longer works, held together as books, with their chapters in order.</p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {books.map((book) => (
                <Reveal key={book.id}>
                  <button data-testid={`book-card-${book.id}`} onClick={() => { setCurrentBook(book); setActiveTab("book-detail"); scrollTop(); }} className={`w-full text-left rounded-3xl border p-7 lg:p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${darkMode ? "bg-[#181C22] border-[#2A2F35] hover:border-[#B85D34]/50" : "bg-[#F4EFE6] border-[#E6DEC7] hover:border-[#B85D34]/50"}`}>
                    <div className="flex gap-6 items-start">
                      {book.cover ? (
                        <img src={mediaSrc(book.cover)} alt={book.title} className="w-24 h-32 rounded-xl object-cover border border-[#E6DEC7] dark:border-[#2A2F35] shrink-0" />
                      ) : (
                        <div className={`w-24 h-32 rounded-xl border flex items-center justify-center shrink-0 ${darkMode ? "border-[#B85D34]/40 bg-[#B85D34]/10" : "border-[#B85D34]/40 bg-[#B85D34]/5"}`}>
                          <span className="font-serif text-3xl text-[#B85D34] italic">{book.title.slice(0, 1)}</span>
                        </div>
                      )}
                      <div className="space-y-2 min-w-0">
                        <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full ${book.status === "live" ? "bg-[#B85D34] text-white" : "bg-[#B85D34]/10 text-[#B85D34]"}`}>{book.status === "live" ? "Available" : "Forthcoming"}</span>
                        <h2 className="font-serif text-2xl lg:text-3xl font-normal leading-snug group-hover:text-[#B85D34] transition-colors">{book.title}</h2>
                        {book.subtitle && <p className="text-sm italic opacity-70">{book.subtitle}</p>}
                        {book.description && <p className="text-sm opacity-75 leading-relaxed font-light line-clamp-3">{book.description}</p>}
                        <p className="text-xs opacity-60">by {book.author}</p>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* VIEW: BOOK DETAIL */}
        {activeTab === "book-detail" && currentBook && (
          <div className="max-w-3xl mx-auto space-y-10 pb-16 animate-fade-in">
            <button data-testid="back-to-books" onClick={() => { setCurrentBook(null); setActiveTab("books"); }} className="inline-flex items-center gap-2 text-sm font-semibold text-[#B85D34] editorial-link"><ArrowLeft size={15} /> All books</button>

            <Reveal>
              <div className={`rounded-3xl border p-8 lg:p-12 space-y-6 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                <div className="flex flex-col sm:flex-row gap-8 items-start">
                  {currentBook.cover ? (
                    <img data-testid="book-detail-cover" src={mediaSrc(currentBook.cover)} alt={currentBook.title} className="w-36 h-48 rounded-2xl object-cover border border-[#E6DEC7] dark:border-[#2A2F35] shrink-0 shadow-md" />
                  ) : (
                    <div className={`w-36 h-48 rounded-2xl border-2 flex items-center justify-center shrink-0 ${darkMode ? "border-[#B85D34]/40 bg-[#B85D34]/10" : "border-[#B85D34]/40 bg-[#B85D34]/5"}`}>
                      <span className="font-serif text-5xl text-[#B85D34] italic">{currentBook.title.slice(0, 1)}</span>
                    </div>
                  )}
                  <div className="space-y-3 min-w-0">
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full ${currentBook.status === "live" ? "bg-[#B85D34] text-white" : "bg-[#B85D34]/10 text-[#B85D34]"}`}>{currentBook.status === "live" ? "Available" : "Forthcoming"}</span>
                    <h1 data-testid="book-detail-title" className="font-serif text-3xl lg:text-4xl font-normal leading-snug">{currentBook.title}</h1>
                    {currentBook.subtitle && <p className="text-base italic opacity-70">{currentBook.subtitle}</p>}
                    {currentBook.description && <p className="text-sm opacity-80 leading-relaxed font-light whitespace-pre-line">{currentBook.description}</p>}
                    <div className="flex items-center gap-3 pt-2">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${darkMode ? "border-[#B85D34]/40 bg-[#B85D34]/15" : "border-[#B85D34]/40 bg-[#B85D34]/10"}`}>
                        <span className="font-serif text-xs text-[#B85D34] font-semibold">{AUTHOR.initials}</span>
                      </div>
                      <span className="text-xs opacity-60 font-serif italic">by {currentBook.author || AUTHOR.name}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {currentBook.introduction && (
              <Reveal>
                <section className="space-y-4">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Introduction</span>
                  <div data-testid="book-introduction" className="font-serif text-base md:text-lg leading-relaxed opacity-90 whitespace-pre-line">{currentBook.introduction}</div>
                </section>
              </Reveal>
            )}

            <Reveal>
              <section className="space-y-4">
                <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Chapters</span>
                {(currentBook.chapters || []).length === 0 ? (
                  <p data-testid="book-chapters-empty" className="text-sm opacity-50 font-serif italic">The chapter list will be announced as the book takes shape.</p>
                ) : (
                  <div className="space-y-3">
                    {[...currentBook.chapters].sort((a, b) => (a.order || 0) - (b.order || 0)).map((chapter, i) => (
                      <div key={i} data-testid={`book-chapter-${i}`} className={`rounded-2xl border p-5 flex items-start gap-4 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                        <span className="font-serif italic text-2xl text-[#B85D34]/50 shrink-0">{String(chapter.order || i + 1).padStart(2, "0")}</span>
                        <div className="min-w-0 flex-1 space-y-1">
                          <h3 className="font-serif text-lg font-medium">{chapter.title}</h3>
                          {chapter.summary && <p className="text-sm opacity-75 font-light">{chapter.summary}</p>}
                          {chapter.essay && <button data-testid={`chapter-essay-${i}`} onClick={() => handleOpenArticle(chapter.essay.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#B85D34] editorial-link">Read as an essay: {chapter.essay.title} <ArrowRight size={12} /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </Reveal>

            {(currentBook.related_essays || []).length > 0 && (
              <Reveal>
                <section className="space-y-4">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Related essays</span>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {currentBook.related_essays.map((essay) => (
                      <button key={essay.id} data-testid={`related-essay-${essay.id}`} onClick={() => handleOpenArticle(essay.id)} className={`text-left rounded-2xl border p-4 space-y-1 transition-colors hover:border-[#B85D34]/50 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                        <span className="font-serif text-base font-medium block">{essay.title}</span>
                        {essay.subtitle && <span className="text-xs italic opacity-60 block">{essay.subtitle}</span>}
                        <span className="text-[11px] opacity-50 block">{essay.readTime}</span>
                      </button>
                    ))}
                  </div>
                </section>
              </Reveal>
            )}
          </div>
        )}

        {/* VIEW: ABOUT */}
        {activeTab === "about" && (
          <div className="max-w-3xl mx-auto space-y-8">
            <Reveal className="space-y-4 text-center">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">About</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">About</h1>
            </Reveal>
            <div className="flex flex-wrap justify-center gap-2">
              {[{ id: "publication", label: "About Mind & Mizan" }, { id: "author", label: "About the Author" }, { id: "philosophy", label: "Author Philosophy" }].map((s) => (
                <button key={s.id} data-testid={`about-tab-${s.id}`} onClick={() => setAboutSection(s.id)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${aboutSection === s.id ? "bg-[#B85D34] text-white shadow-sm" : darkMode ? "bg-[#181C22] border border-[#2A2F35] hover:border-[#B85D34]" : "bg-[#F4EFE6] border border-[#E6DEC7] hover:border-[#B85D34]"}`}>{s.label}</button>
              ))}
            </div>
            {aboutSection === "publication" && (
              <Reveal>
                <div data-testid="about-publication" className={`rounded-3xl border p-8 lg:p-12 font-serif text-base md:text-lg leading-relaxed opacity-90 whitespace-pre-line ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>{siteTexts.about?.publication}</div>
              </Reveal>
            )}
            {aboutSection === "author" && (
              <Reveal>
                <div data-testid="about-author" className={`rounded-3xl border p-8 lg:p-12 space-y-6 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                  <div className="flex items-center gap-6 pb-6 border-b border-[#E6DEC7] dark:border-[#3A4048]">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center border-2 ${darkMode ? "border-[#B85D34]/50 bg-[#B85D34]/10" : "border-[#B85D34]/40 bg-[#B85D34]/5"}`}>
                      <span className="font-serif text-3xl text-[#B85D34] font-medium tracking-tight">{AUTHOR.initials}</span>
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl font-medium">{AUTHOR.name}</h3>
                      <p className="text-xs uppercase tracking-widest text-[#B85D34] font-semibold mt-1">{AUTHOR.role}</p>
                    </div>
                  </div>
                  <div className="font-serif text-base md:text-lg leading-relaxed opacity-90 whitespace-pre-line">{siteTexts.about?.author}</div>
                  <div className="pt-6 border-t border-[#E6DEC7] dark:border-[#3A4048]">{renderFollowRow()}</div>
                </div>
              </Reveal>
            )}
          </div>
        )}

        {/* VIEW 4: AUTHOR PHILOSOPHY, NUMBERED MANIFESTO (under About) */}
        {activeTab === "about" && aboutSection === "philosophy" && (
          <div className="max-w-3xl mx-auto space-y-14 pb-16 mt-8">
            <Reveal className="space-y-4 text-center">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Manifesto & Vision</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">A Note from the Author</h1>
              <p className="opacity-80 max-w-xl mx-auto font-light">
                {siteTexts.manifesto.subtitle}
              </p>
            </Reveal>

            <Reveal>
              <div className={`p-8 lg:p-12 rounded-3xl border space-y-10 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                {/* Author identity, monogram mark */}
                <div className="flex items-center gap-6 pb-8 border-b border-[#E6DEC7] dark:border-[#3A4048]">
                  <div className="relative">
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center border-2 ${darkMode ? "border-[#B85D34]/50 bg-[#B85D34]/10" : "border-[#B85D34]/40 bg-[#B85D34]/5"}`}>
                      <span className="font-serif text-4xl text-[#B85D34] font-medium tracking-tight">{AUTHOR.initials}</span>
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#B85D34] text-white flex items-center justify-center shadow-md">
                      <Feather size={13} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-medium">{AUTHOR.name}</h3>
                    <p className="text-xs uppercase tracking-widest text-[#B85D34] font-semibold mt-1">{AUTHOR.role}</p>
                  </div>
                </div>

                {siteTexts.manifesto.chapters.map((chapter, index) => (
                  <Reveal className="space-y-4" key={index}>
                    <div className="flex items-baseline gap-4">
                      <span className="font-serif text-3xl text-[#B85D34]/50 italic">{String(index + 1).padStart(2, "0")}</span>
                      <h4 className="font-serif text-xl font-medium">{chapter.title}</h4>
                    </div>
                    <p className="font-serif text-base md:text-lg leading-relaxed opacity-90 pl-12">{chapter.text}</p>
                  </Reveal>
                ))}

                {siteTexts.manifesto.humility && (
                  <Reveal>
                    <figure data-testid="humility-note" className={`rounded-3xl border p-8 lg:p-10 space-y-4 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#FBF9F5] border-[#E6DEC7]"}`}>
                      <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">{siteTexts.manifesto.humility_title || "A Note of Humility"}</span>
                      <div className="font-serif text-base md:text-lg leading-relaxed opacity-90 whitespace-pre-line">{siteTexts.manifesto.humility}</div>
                    </figure>
                  </Reveal>
                )}

                <div className="pt-6 border-t border-[#E6DEC7] dark:border-[#3A4048] flex flex-col sm:flex-row gap-3 justify-between sm:items-center text-xs opacity-75">
                  <span>Est. 2024 • Written & published independently by {AUTHOR.name}</span>
                  <span className="font-serif italic">"{siteTexts.manifesto.closing_quote}"</span>
                </div>
              </div>
            </Reveal>
          </div>
        )}

        {/* VIEW: SERIES, NAMED EDITORIAL ARCS */}
        {activeTab === "series" && (
          <div className="max-w-4xl mx-auto space-y-20 pb-16">
            <Reveal className="space-y-4 text-center">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Named Arcs</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">The Series</h1>
              <p className="opacity-80 max-w-2xl mx-auto font-light leading-relaxed">
                Essays at Mind &amp; Mizan gather into named arcs, journeys meant to be walked in order, each movement preparing the next.
              </p>
            </Reveal>

            {seriesData.length === 0 && <div className="py-16 text-center opacity-60 animate-pulse">Gathering the arcs...</div>}

            {seriesData.map((arc) => (
              <div key={arc.name} className="space-y-8">
                <Reveal className="space-y-3 text-center border-b pb-8 border-[#E6DEC7] dark:border-[#2A2F35]">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">{arc.tagline}</span>
                  <h2 data-testid={`series-title-${arc.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="font-serif text-3xl lg:text-4xl font-normal">{arc.name}</h2>
                  {arc.status === "live" ? (
                    <p className="opacity-80 max-w-2xl mx-auto font-light leading-relaxed text-sm">
                      {arc.description}
                    </p>
                  ) : (
                    <p className="opacity-80 max-w-2xl mx-auto font-light leading-relaxed text-sm">
                      {arc.description} A new arc is being written.
                    </p>
                  )}
                  {(() => {
                    if (!reader || arc.essays.length === 0) return null;
                    const read = arc.essays.filter((a) => readIds.includes(a.id)).length;
                    if (read === 0) return null;
                    return (
                      <div data-testid={`reading-progress-${arc.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="pt-3 space-y-2 max-w-xs mx-auto">
                        <div className={`h-[2px] rounded-full overflow-hidden ${darkMode ? "bg-[#2A2F35]" : "bg-[#E6DEC7]"}`}>
                          <div className="h-full bg-[#B85D34]/70 transition-all duration-700" style={{ width: `${(read / arc.essays.length) * 100}%` }} />
                        </div>
                        <p className="text-xs opacity-55 font-serif italic">{read} of {arc.essays.length} movements read{read === arc.essays.length ? ", the arc is yours now" : ""}</p>
                      </div>
                    );
                  })()}
                </Reveal>

                {arc.foreword ? (
                    <Reveal>
                      <figure data-testid="series-foreword" className={`rounded-3xl border p-8 lg:p-10 space-y-4 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">A word before you begin</span>
                        <blockquote className="font-serif text-lg md:text-xl leading-relaxed opacity-90 whitespace-pre-line">{arc.foreword}</blockquote>
                        <figcaption className="flex items-center gap-3 pt-2">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${darkMode ? "border-[#B85D34]/40 bg-[#B85D34]/15" : "border-[#B85D34]/40 bg-[#B85D34]/10"}`}>
                            <span className="font-serif text-xs text-[#B85D34] font-semibold">SK</span>
                          </div>
                          <span className="text-xs opacity-60 font-serif italic">Shailla Khan</span>
                        </figcaption>
                      </figure>
                    </Reveal>
                  ) : arc.status === "live" ? (
                    <Reveal>
                      <div data-testid="series-foreword-placeholder" className={`rounded-3xl border border-dashed p-6 text-center ${darkMode ? "border-[#3A4048]" : "border-[#D9CFB8]"}`}>
                        <p className="text-sm opacity-50 font-serif italic">A personal foreword from Shailla will arrive here soon.</p>
                      </div>
                    </Reveal>
                  ) : null}

                {arc.status === "live" ? (
                  <div className="space-y-6">
                    {arc.essays.map((art, i) => (
                      <Reveal key={art.id} delay={i * 0.06}>
                        <button
                          data-testid={`series-chapter-${art.id}`}
                          onClick={() => handleOpenArticle(art.id)}
                          className={`w-full text-left rounded-3xl border p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${darkMode ? "bg-[#181C22] border-[#2A2F35] hover:border-[#B85D34]/50" : "bg-[#F4EFE6] border-[#E6DEC7] hover:border-[#B85D34]/50"}`}
                        >
                          <div className="flex sm:flex-col items-center gap-3 shrink-0">
                            <span className="font-serif italic text-3xl text-[#B85D34]/50">{String(art.series_order).padStart(2, "0")}</span>
                            {art.cover && <img src={mediaSrc(art.cover)} alt="" className="w-24 h-24 rounded-2xl object-cover border border-[#E6DEC7] dark:border-[#2A2F35]" />}
                          </div>
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-wider text-[#B85D34] font-semibold">
                              <span>Movement {art.series_order}</span>
                              {readIds.includes(art.id) && <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><Check size={12} /> Read</span>}
                            </div>
                            <h3 className="font-serif text-2xl lg:text-3xl font-medium leading-snug group-hover:text-[#B85D34] transition-colors">{art.title}</h3>
                            <p className="text-sm italic opacity-70 font-serif">{art.subtitle}</p>
                            <p className="text-sm opacity-75 font-light leading-relaxed line-clamp-2">{art.excerpt}</p>
                            <p className="text-xs opacity-55 pt-1">{art.readTime} · narration available</p>
                          </div>
                          <ArrowRight size={22} className="text-[#B85D34] shrink-0 group-hover:translate-x-1.5 transition-transform hidden sm:block" />
                        </button>
                      </Reveal>
                    ))}
                    {arc.name === "The Inner Life" && (
                      <Reveal className="text-center">
                        <p className="font-serif italic text-lg opacity-75 max-w-xl mx-auto">"You were not created in vain. The days are eligible. Begin again tomorrow, on purpose."</p>
                        <p className="text-xs uppercase tracking-widest opacity-50 mt-3">from the closing of Made on Purpose</p>
                      </Reveal>
                    )}
                  </div>
                ) : (
                  <Reveal>
                    <div data-testid="forthcoming-arc" className={`rounded-3xl border border-dashed p-8 lg:p-10 space-y-6 ${darkMode ? "border-[#3A4048]" : "border-[#D9CFB8]"}`}>
                      <div className="flex flex-wrap gap-2">
                        {arc.movements.map((m, i) => (
                          <span key={m} className={`px-4 py-2 rounded-full text-xs font-medium border ${darkMode ? "border-[#3A4048] opacity-70" : "border-[#E6DEC7] bg-[#F4EFE6]"}`}>
                            {String(i + 1).padStart(2, "0")} · {m}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm opacity-65 font-light leading-relaxed max-w-xl">
                        The first essay of this arc is being written. Join the Mizan Dispatch below and it will find your inbox the Friday it is published.
                      </p>
                      <button
                        data-testid="forthcoming-notify-btn"
                        onClick={() => { setActiveTab("home"); setTimeout(() => document.getElementById("newsletter-section")?.scrollIntoView({ behavior: "smooth" }), 150); }}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#B85D34] text-[#B85D34] text-sm font-medium hover:bg-[#B85D34] hover:text-white transition-all"
                      >
                        <Mail size={15} /> Notify me when it arrives
                      </button>
                    </div>
                  </Reveal>
                )}
              </div>
            ))}
          </div>
        )}

        {/* VIEW 5: SANCTUARY (BOOKMARKS) */}
        {activeTab === "bookmarks" && (
          <div className="space-y-10 animate-fade-in">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Personal Sanctuary</span>
              <h1 className="font-serif text-4xl lg:text-5xl font-normal">Saved Essays ({bookmarkedIds.length})</h1>
              <p className="opacity-85 max-w-xl font-light">
                Your private collection of bookmarked reflections for deep reading and contemplation.
              </p>
            </div>

            {bookmarkedIds.length === 0 ? (
              <div className={`py-24 text-center rounded-3xl border space-y-6 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
                <Bookmark size={40} className="mx-auto opacity-30 text-[#B85D34]" />
                <div className="space-y-2">
                  <h3 className="font-serif text-2xl font-medium">Your sanctuary is currently empty</h3>
                  <p className="text-sm opacity-70 max-w-md mx-auto font-light">
                    Click the bookmark icon on any essay to save it here for peaceful re-reading whenever you wish.
                  </p>
                </div>
                <button
                  onClick={() => setActiveTab("articles")}
                  className="px-8 py-3 rounded-full bg-[#B85D34] text-white text-sm font-medium hover:bg-[#A04E29] transition-all"
                >
                  Explore Essays
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.filter(a => bookmarkedIds.includes(a.id)).map(art => (
                  <div
                    key={art.id}
                    onClick={() => handleOpenArticle(art.id)}
                    className={`${cardCls} ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold uppercase tracking-wider text-[#B85D34]">{art.topic}</span>
                        <span className="opacity-60">{art.readTime}</span>
                      </div>
                      <h3 className="font-serif text-xl font-medium leading-snug group-hover:text-[#B85D34] transition-colors">
                        {art.title}
                      </h3>
                      <p className="text-sm opacity-85 line-clamp-3 font-light leading-relaxed">
                        {art.excerpt}
                      </p>
                    </div>

                    <div className="pt-6 mt-6 border-t border-[#E6DEC7]/60 dark:border-[#2A2F35] flex items-center justify-between text-xs opacity-80">
                      <span>{art.date}</span>
                      <button
                        onClick={(e) => handleBookmark(art.id, e)}
                        className="text-[#B85D34] font-medium editorial-link flex items-center gap-1"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "library" && reader && (
          <ReaderProfile reader={reader} darkMode={darkMode} onOpenArticle={handleOpenArticle} onSignOut={readerSignOut} />
        )}

        {activeTab === "author" && <AuthorDesk onPublished={fetchArticles} onSiteChanged={() => { fetchSeries(); fetchSiteTexts(); }} />}

      </main>

      {/* FOOTER */}
      <footer className={`mt-32 border-t py-16 px-6 lg:px-16 transition-colors ${darkMode ? "bg-[#15181D] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#B85D34] text-white flex items-center justify-center font-serif text-lg">
                M
              </div>
              <h3 className="font-serif text-xl font-bold">Mind & Mizan</h3>
            </div>
            <p className="text-sm opacity-75 max-w-sm font-light leading-relaxed">
              A contemplative personal publication by {AUTHOR.name} exploring the human mind, Qur'anic reflection, psychology, and the art of character.
            </p>
          </div>

          <div className="md:col-span-3 space-y-3">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B85D34]">Navigation</h4>
            <ul className="space-y-2 text-sm opacity-80">
              <li><button onClick={() => setActiveTab("home")} className="editorial-link hover:text-[#B85D34] transition-colors">Home</button></li>
              <li><button onClick={() => setActiveTab("articles")} className="editorial-link hover:text-[#B85D34] transition-colors">All Essays</button></li>
              <li><button onClick={() => { setActiveTab("about"); setAboutSection("philosophy"); }} className="editorial-link hover:text-[#B85D34] transition-colors">Author Philosophy</button></li>
              <li><button onClick={() => setActiveTab("series")} className="editorial-link hover:text-[#B85D34] transition-colors">Series</button></li>
              <li><button onClick={() => setActiveTab("bookmarks")} className="editorial-link hover:text-[#B85D34] transition-colors">Sanctuary</button></li>
            </ul>
          </div>

          <div className="md:col-span-3 space-y-3">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B85D34]">Topics</h4>
            <ul className="space-y-2 text-sm opacity-80">
              <li><button onClick={() => { setSelectedTopic("The Mind"); setActiveTab("articles"); }} className="editorial-link hover:text-[#B85D34] transition-colors">The Mind</button></li>
              <li><button onClick={() => { setSelectedTopic("Qur'anic Reflection"); setActiveTab("articles"); }} className="editorial-link hover:text-[#B85D34] transition-colors">Qur'anic Reflection</button></li>
              <li><button onClick={() => { setSelectedTopic("Psychology & Neuroscience"); setActiveTab("articles"); }} className="editorial-link hover:text-[#B85D34] transition-colors">Psychology & Neuroscience</button></li>
              <li><button onClick={() => { setSelectedTopic("Character & Life"); setActiveTab("articles"); }} className="editorial-link hover:text-[#B85D34] transition-colors">Character & Life</button></li>
            </ul>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mt-12 pt-8 border-t border-[#E6DEC7]/60 dark:border-[#2A2F35] flex flex-col sm:flex-row items-center justify-between text-xs opacity-60">
          <p>© {new Date().getFullYear()} Mind & Mizan by {AUTHOR.name}. All rights reserved.</p>
          <p className="mt-2 sm:mt-0 font-serif italic">Designed with editorial warmth and contemplative calm.</p>
        </div>
      </footer>
    </div>
  );
}
